import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { AssignmentTemplatesPaginatedData } from '../../model/assignments/interfaces'
import type { ApiCallbacks, ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { MAX_PER_PAGE, toPaginationValue, useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'
import { compactQuery } from '@shared/lib/url'

import { useAssignmentTemplatesStore } from '../../model/assignment-templates/store'

interface AssignmentTemplatesApiOptions extends ApiCallbacks<AssignmentTemplatesPaginatedData> {
  route?: RouteLocationNormalizedGeneric
}

/** Filters, pagination, the route watcher and timezone changes all call this without waiting for
 *  one another; only the newest request may write the store or switch the loader off. */
let requestSeq = 0

/** `GET /admin-api/assignment-templates`. Rows carry taxonomy labels and `is_stale` but no `data`.
 *  Latest-wins: an older response that lands late is returned to its caller but not committed. */
export const useAssignmentTemplatesApi = async (
  options?: AssignmentTemplatesApiOptions,
): Promise<AssignmentTemplatesPaginatedData | null> => {
  const token = ++requestSeq
  const isLatest = (): boolean => token === requestSeq

  const { $axios } = useNuxtApp()
  const toast = useToast()
  const route = options?.route || useRoute()
  const store = useAssignmentTemplatesStore()

  const url = useAdminRoutes('admin.assignment-templates.index', compactQuery({
    search: store.getFilterSearch,
    type: store.getFilterType,
    discipline_code: store.getFilterDisciplineCode,
    grade: store.getFilterGrade,
    curriculum_module_id: store.getFilterModuleId,
    per_page: toPaginationValue(route.query?.per_page, store.getPerPage, MAX_PER_PAGE),
    page: toPaginationValue(route.query?.page, store.getCurrentPage),
  }))

  options?.onStart?.()
  store.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options?.onError?.(msg)
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      options?.onError?.(data.message)
      return null
    }

    const paginatedData: AssignmentTemplatesPaginatedData | null = data.data || null

    if (!isLatest()) {
      return paginatedData
    }
    store.setTemplatesPaginatedData(paginatedData)
    options?.onSuccess?.(paginatedData)
    return paginatedData
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options?.onError?.(msg)
    return null
  } finally {
    if (isLatest()) {
      store.setLoading(false)
    }
    options?.onFinish?.()
  }
}
